import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../common/page_component/index.js';
import Loader from "../common/Loader";
import Alert from '../common/Alert.js';
import vehicleService from '../../services/vehicleService.js';
import { Truck, ArrowLeft, Pencil } from "lucide-react";
import Button from '../common/Button.js';
import { formatLocationPointsDisplay } from '../../utils/vehicleLocationPoints.js';
import { formatVehicleType } from '../../utils/vehicleTypes.js';


export default function ViewVehicle() {
  const { vehicleId } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchVehicleDetails = useCallback(async () => {
    try {
      console.log("[v0] Fetching vehicle details for ID:", vehicleId);
      setLoading(true);
      const response = await vehicleService.getVehicleById(vehicleId);
      console.log("[v0] Vehicle fetched successfully:", response.data.data);
      setVehicle(response.data.data);
    } catch (err) {
      console.error("[v0] Error fetching vehicle:", err);
      Alert.error(err.response?.data?.error || 'Failed to load vehicle details');
      navigate('/vehicles');
    } finally {
      setLoading(false);
    }
  }, [vehicleId, navigate]);

  useEffect(() => {
    console.log("[v0] ViewVehicle mounted with vehicleId:", vehicleId);
    fetchVehicleDetails();
  }, [fetchVehicleDetails, vehicleId]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center py-20">
          <Loader fullScreen={false} size="default" message="Loading vehicle details..." />
        </div>
      </DashboardLayout>
    );
  }

  if (!vehicle) {
    return (
      <DashboardLayout>
        <div className="max-w-4xl mx-auto w-full min-w-0">
          <button
            onClick={() => navigate('/vehicles')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Vehicles
          </button>
          <div className="bg-white rounded-xl shadow p-6 text-center">
            <p className="text-gray-600">Vehicle not found</p>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto w-full min-w-0">
        {/* Back Button */}
        <button
          onClick={() => navigate('/vehicles')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 mb-6 transition font-medium"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to Vehicles
        </button>

        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <Truck className="w-8 h-8 text-buttonBg flex-shrink-0" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-secondary">{vehicle.vehicle_name || vehicle.vehicle_number}</h1>
                <p className="text-gray-600 text-sm sm:text-base">{vehicle.vehicle_number}</p>
              </div>
            </div>
            <span className={`px-4 py-2 rounded-full text-sm font-semibold whitespace-nowrap ${
              vehicle.status.toLowerCase() === 'active' 
                ? 'bg-green-100 text-green-800' 
                : 'bg-red-100 text-red-800'
            }`}>
              {vehicle.status.toUpperCase()}
            </span>
          </div>
        </div>

        {/* Main Details */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4 flex items-center gap-2">
            <Truck className="w-5 h-5 text-secondary" />
            Vehicle Information
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4">
            {/* Vehicle Name */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Vehicle Name</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{vehicle.vehicle_name}</p>
            </div>

            {/* Vehicle Number */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Vehicle Number</p>
              <p className="font-semibold text-secondary text-base sm:text-lg font-mono">{vehicle.vehicle_number}</p>
            </div>

            {/* Vehicle Type */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Vehicle Type</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">{formatVehicleType(vehicle.vehicle_type)}</p>
            </div>

            {/* Vehicle Size */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Vehicle Size (Ft)</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {vehicle.vehicle_size != null ? vehicle.vehicle_size : '—'}
              </p>
            </div>

            {/* Capacity */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Capacity (MT)</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {vehicle.capacity != null ? vehicle.capacity : '—'}
              </p>
            </div>

            {/* Status */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Status</p>
              <p className={`font-semibold text-base sm:text-lg uppercase ${
                vehicle.status.toLowerCase() === 'active' ? 'text-green-700' : 'text-red-700'
              }`}>
                {vehicle.status}
              </p>
            </div>

            {/* Vehicle Contract Amount */}
            {vehicle.vehicle_contract_amount && (
              <div className="bg-gray-50 rounded-xl p-4 border border-primary">
                <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">Contract Amount</p>
                <p className="font-semibold text-secondary text-base sm:text-lg">
                  ₹{Number(vehicle.vehicle_contract_amount).toLocaleString('en-IN')}
                </p>
              </div>
            )}

            {/* Pickup Point */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">
                Pickup Point{formatLocationPointsDisplay(vehicle.pickup_point).labels.length > 1 ? 's' : ''}
              </p>
              {formatLocationPointsDisplay(vehicle.pickup_point).labels.length === 0 ? (
                <p className="font-semibold text-secondary text-base sm:text-lg">—</p>
              ) : (
                <ul className="space-y-1.5 max-h-[9.5rem] overflow-y-auto pr-1">
                  {formatLocationPointsDisplay(vehicle.pickup_point).labels.map((loc, idx) => (
                    <li
                      key={`pickup-${idx}`}
                      className="font-medium text-secondary text-sm sm:text-base bg-white rounded-lg px-3 py-1.5 border border-gray-100"
                    >
                      {loc}
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {/* Drop Point */}
            <div className="bg-gray-50 rounded-xl p-4 border border-primary">
              <p className="text-xs sm:text-sm font-semibold text-secondary uppercase tracking-wide mb-2">
                Drop Point{formatLocationPointsDisplay(vehicle.drop_point).labels.length > 1 ? 's' : ''}
              </p>
              {formatLocationPointsDisplay(vehicle.drop_point).labels.length === 0 ? (
                <p className="font-semibold text-secondary text-base sm:text-lg">—</p>
              ) : (
                <ul className="space-y-1.5 max-h-[9.5rem] overflow-y-auto pr-1">
                  {formatLocationPointsDisplay(vehicle.drop_point).labels.map((loc, idx) => (
                    <li
                      key={`drop-${idx}`}
                      className="font-medium text-secondary text-sm sm:text-base bg-white rounded-lg px-3 py-1.5 border border-gray-100"
                    >
                      {loc}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>

        {/* Additional Information */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 mb-6">
          <h2 className="text-lg font-bold text-secondary mb-4">Additional Information</h2>

          <div className="grid grid-cols-2 md:grid-cols-2 gap-2 sm:gap-4">
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Vehicle ID</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">#{vehicle.id}</p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Created Date</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {new Date(vehicle.created_at).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>

            <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
              <p className="text-xs sm:text-sm font-semibold text-gray-700 uppercase tracking-wide mb-2">Last Updated</p>
              <p className="font-semibold text-secondary text-base sm:text-lg">
                {new Date(vehicle.updated_at).toLocaleDateString('en-IN', {
                  day: '2-digit',
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-3">
          <Button variant="secondary" icon={Pencil} onClick={() => navigate(`/vehicles/edit/${vehicle.id}`)}>
            Edit Vehicle
          </Button>
          <Button variant="secondary" onClick={() => navigate('/vehicles')}>
            Back to List
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}